import { Button } from '@components/Button/';
import Block from '@components/Layout/Block';
import { Statistic } from '@components/MiscComponents/Statistic';
import { color, font, query, space } from '@design/templateFns';
import { FC } from 'react';
import styled from 'styled-components';
import { Props } from '../../BlockMapper';

const VariantStackedTextAndImageBlock: FC<Props> = (props) => {
  const { eyebrow, header, img, bodyText, ctaText, ctaUrl, statHeader, stats } = props;
  return (
    <Block narrow background="secondaryBg">
      <Container>
        <GridLeftContainer>
          {statHeader && <Eyebrow>{statHeader}</Eyebrow>}
          <Wrapper>
            {stats &&
              stats.map((stat) => {
                return (
                  <Statistic
                    key={stat.subheader}
                    statNumber={stat.statNumber}
                    subheader={stat.subheader}
                    prepend={stat.prepend}
                    append={stat.append}
                  />
                );
              })}
          </Wrapper>
        </GridLeftContainer>
        <Line />
        <GridRightContainer>
          <ImageBox> {img && <img src={img} />}</ImageBox>
          <ContentContainer>
            {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
            {header && <Header>{header}</Header>}
            <Text>{bodyText as string}</Text>
            {ctaUrl && ctaText && (
              <Button arrow={true} variant={'tertiaryGray'} url={ctaUrl} label={ctaText} />
            )}
          </ContentContainer>
        </GridRightContainer>
      </Container>
    </Block>
  );
};

export default VariantStackedTextAndImageBlock;

const Wrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-gap: ${space('margin')};

  @media screen and (${query.atLeast('desktop')}) {
    grid-template-columns: none;
    grid-template-rows: repeat(4, 1fr);
  }
`;

export const Line = styled.div`
  background: linear-gradient(red, red) no-repeat center/1px 100%;
  display: block;
  height: 100px;
  margin: 50px;
  @media screen and (${query.atLeast('desktop')}) {
    height: inherit;
    margin: 0;
  }
`;
export const Container = styled.div`
  display: grid;

  @media screen and (${query.atLeast('desktop')}) {
    grid-template-columns: 45% 10% 45%;
  }
`;

export const GridLeftContainer = styled.div<{ $reverse?: boolean }>`
  display: flex;
  flex-direction: column;
  max-width: 400px;
  margin: auto;
  text-align: center;

  @media screen and (${query.atLeast('desktop')}) {
    flex-direction: ${({ $reverse }) => ($reverse ? 'column-reverse' : 'column')};
  }
`;

export const GridRightContainer = styled.div`
  display: flex;
  gap: 50px;
  flex-direction: column;
  max-width: 400px;
  margin: auto;
  align-items: center;

  @media screen and (${query.atLeast('desktop')}) {
    gap: 80px;
  }
`;

export const ContentContainer = styled.div<{ $reverse?: boolean }>`
  display: flex;
  justify-content: center;
  flex-direction: column;
  grid-row: 2;
  text-align: center;

  @media screen and (${query.atLeast('tablet')}) {
    margin-top: 0;
    grid-row: ${({ $reverse }) => ($reverse ? 1 : 'unset')};
  }
`;

export const Eyebrow = styled.h3`
  text-transform: uppercase;
  color: ${color('cta')};
  ${font('eyebrow-sm')};
  padding-bottom: ${space('x6')};
  margin-top: 0;

  @media screen and (${query.atLeast('desktop')}) {
    ${font('eyebrow-md')};
  }
`;

export const Header = styled.h2`
  ${font('headline-xxs')};
  margin: 0 0 32px;
  padding: 0;

  @media screen and (${query.atLeast('tablet')}) {
    ${font('headline-sm')};
  }
`;

export const Text = styled.div`
  color: ${color('fg')};
  padding-bottom: 42px;
  ${font('body-sm-light')};

  @media screen and (${query.atLeast('tablet')}) {
    ${font('body-md-light')};
  }
`;

export const StyledSpan = styled.span`
  ${font('headline-xs')};
  color: ${color('cta')};
  margin-right: 9px;
  position: relative;
  bottom: 1px;
  cursor: pointer;
`;

export const ImageBox = styled.div`
  img {
    width: 250px;
    height: 250px;
    top: 1px;
    left: 1px;
    object-fit: cover;
    object-position: center;
    border-radius: 50%;
    box-sizing: border-box;
  }
`;
